import { forwardRef } from "react";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@mantine/core";

import { AggregateIssueState } from "@protos/issues/v1/issues";
import {
  TriageStateButtonIconMapping,
  TriageStateColorMapping,
} from "@shared/types";

export interface ImprovedInlineTriagePopoverTargetProps {
  issueState: AggregateIssueState;
  isFixed: boolean;
  isRemoved: boolean;
  onClick: () => void;
  isLoading: boolean;
}

/**
 * The inline triage popover target - only used in individual finding cards.
 * @finding A formatting finding.
 * @onClick A function to set the state to determine when the popover dropdown will be displayed.
 * @isLoading If true, the component shows a loading state. If false, it shows the default state.
 *
 */
export const ImprovedInlineTriagePopoverTarget = forwardRef<
  HTMLButtonElement,
  ImprovedInlineTriagePopoverTargetProps
>(({ issueState, isFixed, isRemoved, onClick, isLoading }, ref) => {
  let buttonText = "";
  let buttonColor = "";
  let buttonIcon = TriageStateButtonIconMapping["ignored"]; // default for ignored
  let buttonTextColor = "white";

  if (isFixed) {
    buttonText = "Fixed";
    buttonColor = TriageStateColorMapping["fixed"];
    buttonIcon = TriageStateButtonIconMapping["fixed"];
    buttonTextColor = "green.1";
  } else if (isRemoved) {
    buttonText = "Removed";
    buttonColor = TriageStateColorMapping["removed"];
    buttonIcon = TriageStateButtonIconMapping["removed"];
    buttonTextColor = "gray.3";
  } else if (issueState === AggregateIssueState.OPEN) {
    buttonText = "Open";
    buttonColor = TriageStateColorMapping["reopened"];
    buttonIcon = TriageStateButtonIconMapping["reopened"];
  } else if (issueState === AggregateIssueState.REVIEWING) {
    buttonText = "Reviewing";
    buttonColor = TriageStateColorMapping["reviewing"];
    buttonIcon = TriageStateButtonIconMapping["reviewing"];
  } else if (issueState === AggregateIssueState.FIXING) {
    buttonText = "Fixing";
    buttonColor = TriageStateColorMapping["fixing"];
    buttonIcon = TriageStateButtonIconMapping["fixing"];
  } else {
    buttonText = "Ignored";
    buttonColor = TriageStateColorMapping["ignored"];
  }

  const isDisabled = isFixed || isRemoved;
  return (
    <Button
      data-test-id="inline-triage-button"
      ref={ref}
      onClick={onClick}
      leftSection={!isRemoved && <FontAwesomeIcon icon={buttonIcon} />}
      rightSection={!isDisabled && <FontAwesomeIcon icon={faChevronDown} />}
      loading={isLoading}
      color={buttonColor}
      disabled={isDisabled}
      styles={{
        root: {
          backgroundColor: buttonColor,
          color: buttonTextColor,
        },
      }}
    >
      {buttonText}
    </Button>
  );
});
